import React, { type ReactNode, forwardRef, type FunctionComponent, useState } from 'react'
import { css } from '@emotion/react'
import get from 'lodash/get'
import { Text, spacing, colors, fontWeight, fontSize } from '@retailer-platform/shared-components'
import { type ControllerProps } from 'utils/react-hook-form/reactHookForm.types'
import { useDashMessages } from 'intl/intl.hooks'
import { type DashMessages } from 'intl/intl.types'
import { ImageUploader } from 'gin-and-tonic/containers/image-uploader/ImageUploader'
import { ErrorMessage } from 'gin-and-tonic/common/error-message/ErrorMessage'
import {
  type RequiredImageDimensions,
  type ImageDimensions,
} from 'gin-and-tonic/containers/image-uploader/utils/ImageUploader.types'
import clientEnv from 'utils/global/clientEnv'
import { useDashIntl } from 'intl/intl.hooks'
import { useFormErrorMessageMap } from 'utils/react-hook-form/reactHookForm.hooks'
import {
  type FormErrorMessageMap,
  type ErrorRecordType,
} from 'utils/react-hook-form/reactHookForm.types'

export interface Props extends Omit<ControllerProps, 'name'> {
  identifier: string
  previewDimensions: ImageDimensions
  imageDimensions: RequiredImageDimensions
  labelId: DashMessages
  labelPrefix?: ReactNode
  defaultUrl?: string
  errors?: ErrorRecordType
  errorMessages?: FormErrorMessageMap
}

const defaultErrorMessages: Record<string, DashMessages> = {
  required: 'merchandising.hero-banners.form.validation.required',
}

const rootStyles = css({
  display: 'flex',
})

const detailStyles = css({
  display: 'flex',
  flexDirection: 'column',
  width: 315,
  marginRight: spacing.X16,
})

const labelStyles = css({
  color: colors.GRAYSCALE.X70,
  marginBottom: spacing.X4,
  fontWeight: fontWeight.SEMIBOLD,
})

const sublabelStyles = css({
  color: colors.GRAYSCALE.X70,
  marginBottom: spacing.X4,
})

const errorMessageStyles = css({
  fontSize: fontSize.X10,
  marginLeft: 0,
  marginRight: 0,
})

const previewStyles = ({ width, height }) =>
  css({
    width,
    minHeight: height,
    display: 'flex',
  })

export const HeroBannerFormContentImageInput: FunctionComponent<Props> = forwardRef<
  HTMLInputElement,
  Props
>(
  (
    {
      errors,
      identifier,
      previewDimensions,
      imageDimensions,
      labelId,
      labelPrefix = '',
      defaultUrl = '',
    },
    ref
  ) => {
    const messages = useDashMessages({
      label: labelId,
      sublabel: 'merchandising.hero-banners.section.content.image.acceptedFormat',
      uploaded: 'merchandising.hero-banners.section.content.image.uploaded',
      browse: 'common.browse',
      remove: 'merchandising.hero-banners.section.content.image.remove',
    })

    const [imageName, setImageName] = useState<string>('')
    const [imageUrl, setImageUrl] = useState<string>(defaultUrl)
    const sublabel = imageName ? `${messages.uploaded}: ${imageName}` : messages.sublabel

    const handleOnChange = (url, file) => {
      setImageUrl(url || '')
      setImageName(file?.name || '')
    }

    const intl = useDashIntl()

    const errorMessageId = useFormErrorMessageMap(defaultErrorMessages)(get(errors, identifier))
    const formattedMessage =
      errorMessageId && intl.formatMessage({ id: errorMessageId }, { label: messages.label })

    return (
      <div key={labelId} css={rootStyles}>
        <input type="hidden" name={identifier} value={imageUrl} ref={ref} />

        <div css={detailStyles}>
          <Text styles={labelStyles} size={'medium'}>
            {labelPrefix}
            {messages.label}
          </Text>
          <Text styles={sublabelStyles} size={'small'}>
            {sublabel}
          </Text>
        </div>

        <div>
          <div css={previewStyles(previewDimensions)}>
            <ImageUploader
              filestackApiKey={clientEnv.PUBLIC_CLIENT_FILEPICKER_API_KEY}
              requiredDimensions={imageDimensions}
              value={defaultUrl}
              onChange={handleOnChange}
            />
          </div>
          {formattedMessage && (
            <ErrorMessage styles={errorMessageStyles}>{formattedMessage}</ErrorMessage>
          )}
        </div>
      </div>
    )
  }
)
